import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { EnrollmentWizardSteps, EnrollmentWizardStep } from "./EnrollmentWizardSteps";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { AbmConfigStep } from "./steps/AbmConfigStep";
import { SetupAssistantStep } from "./steps/SetupAssistantStep";
import { SupportInfoStep } from "./steps/SupportInfoStep";
import { DeviceAssignmentStep } from "./steps/DeviceAssignmentStep";
import { ReviewStep } from "./steps/ReviewStep";
import { useAdeDevices } from "@/hooks/useAdeDevices";

const formSchema = z.object({
  customer_id: z.string().min(1, { message: "Customer is required" }),
  profile_name: z.string().min(1, { message: "Profile name is required" }),
  description: z.string().optional(),
  department: z.string().optional(),
  abm_connection_id: z.string().optional(),
  is_mandatory: z.boolean().default(true),
  is_mdm_removable: z.boolean().default(false),
  await_device_configured: z.boolean().default(false),
  auto_advance_setup: z.boolean().default(false),
  skip_setup_items: z.array(z.string()).default([]),
  support_phone_number: z.string().optional(),
  support_email_address: z.string().email().optional().or(z.literal("")),
  configuration_web_url: z.string().url().optional().or(z.literal("")),
});

type FormValues = z.infer<typeof formSchema>;

interface InitialData {
  id: string;
  customer_id: string;
  profile_name: string;
  description?: string;
  department?: string;
  abm_connection_id: string;
  is_mandatory?: boolean;
  is_mdm_removable?: boolean;
  await_device_configured?: boolean;
  auto_advance_setup?: boolean;
  skip_setup_items?: string[];
  support_phone_number?: string;
  support_email_address?: string;
  configuration_web_url?: string;
  profile_uuid?: string;
}

interface EnrollmentProfileWizardProps {
  onSave: (profileData: any) => Promise<any>;
  mode?: "create" | "edit";
  initialData?: InitialData;
  initialSelectedDevices?: string[];
}

export const EnrollmentProfileWizard = ({ 
  onSave, 
  mode = "create",
  initialData,
  initialSelectedDevices = []
}: EnrollmentProfileWizardProps) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<EnrollmentWizardStep>("basic");
  const [completedSteps, setCompletedSteps] = useState<EnrollmentWizardStep[]>(
    mode === "edit" ? ["basic", "abm", "setup", "support", "devices"] : []
  );
  const [selectedDevices, setSelectedDevices] = useState<string[]>(initialSelectedDevices);
  const { assignProfile } = useAdeDevices();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_id: initialData?.customer_id || "",
      profile_name: initialData?.profile_name || "",
      description: initialData?.description || "",
      department: initialData?.department || "",
      abm_connection_id: initialData?.abm_connection_id || "",
      is_mandatory: initialData?.is_mandatory ?? true,
      is_mdm_removable: initialData?.is_mdm_removable ?? false,
      await_device_configured: initialData?.await_device_configured ?? false,
      auto_advance_setup: initialData?.auto_advance_setup ?? false,
      skip_setup_items: initialData?.skip_setup_items || [],
      support_phone_number: initialData?.support_phone_number || "",
      support_email_address: initialData?.support_email_address || "",
      configuration_web_url: initialData?.configuration_web_url || "",
    },
  });

  const steps: EnrollmentWizardStep[] = ["basic", "abm", "setup", "support", "devices", "review"];

  const canProceedFromStep = (step: EnrollmentWizardStep): boolean => {
    switch (step) {
      case "basic":
        const { customer_id, profile_name } = form.getValues();
        return !!customer_id && !!profile_name && profile_name.trim().length > 0;
      case "abm":
        const { abm_connection_id } = form.getValues();
        return !!abm_connection_id;
      case "setup":
      case "support":
        return true;
      case "devices":
        return true; // Can skip device assignment
      case "review":
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) {
      form.trigger();
      return;
    }

    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps(prev => [...prev, currentStep]);
    }

    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleDeploy = async () => {
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }

    try {
      const formData = form.getValues();
      const isDraft = selectedDevices.length === 0;
      
      // Create or update the enrollment profile
      const profileData = {
        customer_id: formData.customer_id,
        profile_name: formData.profile_name,
        description: formData.description,
        department: formData.department,
        abm_connection_id: formData.abm_connection_id,
        is_mandatory: formData.is_mandatory,
        is_mdm_removable: formData.is_mdm_removable,
        await_device_configured: formData.await_device_configured,
        auto_advance_setup: formData.auto_advance_setup,
        skip_setup_items: formData.skip_setup_items,
        support_phone_number: formData.support_phone_number,
        support_email_address: formData.support_email_address,
        configuration_web_url: formData.configuration_web_url,
        is_draft: isDraft,
      };

      const savedProfile = await onSave(profileData);
      
      // Get the profile UUID (either from saved response or initial data in edit mode)
      const profileUuid = savedProfile?.profile_uuid || initialData?.profile_uuid;
      
      // Assign devices if any selected
      if (selectedDevices.length > 0 && profileUuid) {
        try {
          await assignProfile(selectedDevices, profileUuid);
          if (mode === "edit") {
            toast.success(`Enrollment profile updated and assigned to ${selectedDevices.length} device(s)`);
          } else {
            toast.success(`Enrollment profile created and assigned to ${selectedDevices.length} device(s)`);
          }
        } catch (error) {
          toast.warning(`Profile ${mode === "edit" ? "updated" : "created"} but device assignment failed`);
        }
      } else {
        if (mode === "edit") {
          toast.success("Enrollment profile updated successfully");
        } else {
          toast.success("Enrollment profile created as draft. Assign devices to activate.");
        }
      }
      
      navigate("/profiles?tab=enrollment");
    } catch (error) {
      console.error(`Error ${mode === "edit" ? "updating" : "creating"} enrollment profile:`, error);
      toast.error(`Failed to ${mode === "edit" ? "update" : "create"} enrollment profile`);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "basic":
        return <BasicInfoStep form={form} />;
      case "abm":
        return (
          <AbmConfigStep
            selectedAbmConnectionId={form.watch("abm_connection_id") || ""}
            onSelectAbmConnection={(id) => form.setValue("abm_connection_id", id)}
            customerId={form.watch("customer_id")}
          />
        );
      case "setup":
        return <SetupAssistantStep form={form} />;
      case "support":
        return <SupportInfoStep form={form} />;
      case "devices":
        return (
          <DeviceAssignmentStep
            selectedDevices={selectedDevices}
            onSelectDevices={setSelectedDevices}
            abmConnectionId={form.watch("abm_connection_id") || ""}
          />
        );
      case "review":
        return (
          <ReviewStep
            formData={form.getValues()}
            selectedDevices={selectedDevices}
          />
        );
      default:
        return null;
    }
  };

  const isEditMode = mode === "edit";
  const headerTitle = isEditMode ? "Edit Enrollment Profile" : "Create Enrollment Profile";
  const headerSubtitle = isEditMode 
    ? "Modify enrollment profile settings and device assignments"
    : "Configure automated device enrollment settings";
  const submitButtonText = isEditMode ? "Update Profile" : "Create Profile";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/profiles?tab=enrollment")}
            >
              <X className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">{headerTitle}</h1>
              <p className="text-sm text-muted-foreground">
                {headerSubtitle}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {currentStep === "review" ? (
              <Button 
                onClick={handleDeploy}
                disabled={!canProceedFromStep(currentStep)}
              >
                {submitButtonText}
              </Button>
            ) : (
              <>
                {currentStep !== "basic" && (
                  <Button 
                    variant="outline"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                )}
                <Button 
                  onClick={handleNext}
                  disabled={!canProceedFromStep(currentStep)}
                >
                  Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <EnrollmentWizardSteps 
        currentStep={currentStep}
        completedSteps={completedSteps}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()}>
              {renderStepContent()}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};
